import { Directive, computed, inject, input } from '@angular/core';
import { WorkflowGraphStore } from '../../../store/workflow-graph.store';
import { WorkflowEditorService } from '../../../store/workflow-editor.service';
import { ToastService } from '../../../../../../../shared/services/toast.service';
import { WorkflowGraphNode } from '../../../models/flow-graph.model';
import { WorkflowPropertyOption, findPropertyDescriptor } from '../../../models/workflow-variable-catalog';
import { WorkflowConfigOption, conditionOperatorOptionsFor } from './config-options';

/**
 * Base común de los editores de configuración por subtipo de nodo del builder de workflow.
 *
 * Es el equivalente workflow de `chatflows/.../node-config/node-config-base.ts`: sustituye
 * al `switch` de ~2.100 líneas del `workflow-node-config-panel` monolítico por un componente
 * pequeño por subtipo, todos extendiendo esta clase.
 *
 * Flujo de guardado DEFINITIVO (Fase 3): la config se edita EN EL STORE
 * (`updateNodeConfig`) y se persiste por el PUT atómico del grafo (`queueAutosave`), igual
 * que chatflow. Ya NO se guarda contra los endpoints por-categoría del panel viejo.
 *
 * Los cambios de config MEZCLAN sobre el objeto existente (que ya trae la forma correcta del
 * `defaultConfig` del catálogo o del backend), así se preserva 1:1 la forma que producía el
 * `buildConfig` del panel viejo sin reconstruirla campo por campo.
 *
 * Gotcha §6: si un cambio de config elimina un puerto (una rama de MULTI_BRANCH, un valor de
 * AB_SPLIT, un branchKey renombrado…), NO se borra la arista en silencio: el canvas la
 * conserva colgada de su puerto huérfano para que el usuario la vea y decida borrarla, de
 * modo que el guardado REPLACE nunca pierde una rama sin intervención.
 */
@Directive()
export abstract class WorkflowNodeConfigBase {
  protected readonly store = inject(WorkflowGraphStore);
  protected readonly editor = inject(WorkflowEditorService);
  protected readonly toast = inject(ToastService);

  readonly node = input.required<WorkflowGraphNode>();

  protected readonly config = computed<Record<string, unknown>>(() => this.node().config ?? {});
  protected readonly readOnly = computed(() => this.editor.readOnly());

  /** Clases Tailwind compartidas (literales estáticos: Tailwind v4 no detecta concatenaciones). */
  protected readonly fieldClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';
  protected readonly labelClass = 'mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300';
  protected readonly hintClass = 'mt-1 text-[11px] text-gray-400 dark:text-gray-500';

  /**
   * Operadores de comparación ofrecidos para una propiedad concreta
   * (INC_CRM_020 (13) inc52/54/56/57/58/60/61).
   *
   * Todos los editores de condición pintaban los TRECE operadores del contrato sin mirar qué
   * propiedad se estaba comparando, así que se podía configurar «Empresa es mayor que…» o
   * «Estado está entre…»: combinaciones que el motor resuelve en silencio contra un id o sin
   * extremos, dejando una rama que nunca se cumple. `conditionOperatorOptionsFor` acota la lista
   * a la forma del valor y conserva el operador ya guardado aunque no encaje.
   *
   * `options` es la MISMA lista que alimenta a `app-wf-property-select` y a
   * `app-wf-condition-value` en ese formulario (la del objeto base, o la del objeto asociado en
   * la condición por asociado): las tres vistas tienen que coincidir en qué propiedad es cuál.
   */
  protected operatorOptionsFor(
    options: readonly WorkflowPropertyOption[],
    property: string,
    current: string
  ): ReadonlyArray<WorkflowConfigOption> {
    const descriptor = findPropertyDescriptor(options, property);
    if (!descriptor) {
      // Propiedad que el catálogo no conoce (atributo personalizado, clave escrita a mano): no se
      // sabe qué forma tiene su valor, así que se ofrecen los trece. Restringir a ciegas escondería
      // el operador que esa propiedad sí necesita.
      return conditionOperatorOptionsFor(undefined, current);
    }
    // Una propiedad del catálogo SIN `kind` es texto por convención —así se declaran `subject`,
    // `name` o `email`, sin repetir `kind: 'TEXT'` en cada línea—, y es lo que ya asume el editor
    // de valor. Sin este default caía en el caso «desconocida» y volvía a ofrecer «es mayor que»
    // sobre el asunto de un ticket.
    return conditionOperatorOptionsFor(descriptor.kind ?? 'TEXT', current);
  }

  // ── Lectura de config con valores por defecto (espeja el loadFromNode del panel viejo) ──

  protected str(key: string, fallback = ''): string {
    const value = this.config()[key];
    return value == null ? fallback : String(value);
  }

  protected num(key: string, fallback = 0): number {
    const value = this.config()[key];
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (value != null && value !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
    return fallback;
  }

  protected bool(key: string, fallback = false): boolean {
    const value = this.config()[key];
    return typeof value === 'boolean' ? value : fallback;
  }

  protected nested(parent: string): Record<string, unknown> {
    const value = this.config()[parent];
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  }

  protected nestedStr(parent: string, key: string, fallback = ''): string {
    const value = this.nested(parent)[key];
    return value == null ? fallback : String(value);
  }

  protected nestedNum(parent: string, key: string, fallback = 0): number {
    const value = this.nested(parent)[key];
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (value != null && value !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
    return fallback;
  }

  protected arr(key: string): unknown[] {
    const value = this.config()[key];
    return Array.isArray(value) ? value : [];
  }

  /** Objeto de config → JSON legible (para los pocos campos que aún se editan como JSON). */
  protected jsonOf(key: string, fallback = '{}'): string {
    const value = this.config()[key];
    if (value && typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  // ── Escritura (store → autosave por PUT del grafo) ──────────────────────────

  protected applyConfig(config: Record<string, unknown>): void {
    if (this.readOnly()) {
      return;
    }
    this.store.updateNodeConfig(this.node().nodeKey, config);
    this.editor.queueAutosave();
  }

  /** Mezcla claves sueltas sobre la config actual. */
  protected patchConfig(patch: Record<string, unknown>): void {
    this.applyConfig({ ...this.config(), ...patch });
  }

  /** Mezcla claves dentro de un objeto anidado (target, source, due, start, window…). */
  protected patchNested(parent: string, patch: Record<string, unknown>): void {
    this.patchConfig({ [parent]: { ...this.nested(parent), ...patch } });
  }

  /** Guarda un objeto JSON parseado bajo `key`; ignora silenciosamente el JSON inválido. */
  protected setJson(key: string, raw: string): void {
    try {
      const parsed = JSON.parse(raw || '{}');
      this.patchConfig({ [key]: parsed });
    } catch {
      // JSON en curso de edición: el aviso lo da configWarnings de cada componente.
    }
  }

  // ── Helpers de eventos del DOM ──────────────────────────────────────────────

  protected inputValue(event: Event): string {
    return (event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
  }

  protected checkboxValue(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  protected numberValue(event: Event): number {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
